import { logger } from '../../../lib/logger';

/**
 * Pure helpers for layouts that declare a fixed column set (Signal Response).
 * `fixed` is (a) the REQUIRED set that is always drawn and (b) the DEFAULT
 * order. Every fixed column can be
 * reordered by the rep, freely interleaved with their own columns; the order
 * lives in `machine.working.columns` like any other view order, so there is
 * no extra storage. The table draws `mergeFixedColumns(fixed, working)` at
 * render time and never rewrites `working` on mount, so a view that was never
 * reordered draws the registry default order and never reads "Modified".
 */

/**
 * `order` (de-duplicated) with every `fixed` key it does not hold inserted at
 * its default relative slot: right after the nearest preceding fixed key that
 * is present (or first, when none precedes it). Fixed keys are processed in
 * default order, so a run of missing keys lands in order.
 */
function insertMissingFixed(fixed: readonly string[], order: readonly string[]): string[] {
  const out = [...new Set(order)];
  fixed.forEach((key, i) => {
    if (out.includes(key)) return;
    let at = 0;
    for (let j = i - 1; j >= 0; j -= 1) {
      const prev = out.indexOf(fixed[j]);
      if (prev >= 0) {
        at = prev + 1;
        break;
      }
    }
    out.splice(at, 0, key);
  });
  return out;
}

/**
 * What the table draws: the view's working order for the keys it holds, with
 * any fixed key it does not hold at its default relative slot. Nothing is
 * frozen, so every fixed column (Signal included) sits wherever the rep put
 * it. Same `working` reference when `fixed` is empty.
 */
export function mergeFixedColumns(fixed: readonly string[], working: string[]): string[] {
  if (fixed.length === 0) return working;
  return insertMissingFixed(fixed, working);
}

/**
 * Inverse of the merge for the wrapped `onVisibleColumnsChange`. The Columns
 * manager (or a header drag) commits the full drawn order; that order IS the
 * saved order. A fixed column the commit omits is re-inserted at its default
 * relative slot (a fixed column can never be dropped).
 */
export function restoreWorkingColumns(
  fixed: readonly string[],
  managerColumns: readonly string[],
): string[] {
  return insertMissingFixed(fixed, managerColumns);
}

/**
 * CDR / GS freeze `contact_name` first, but a view reordered under Signal
 * Response may hold it later. Draw it first there (render-time only; the saved
 * order is untouched). Same reference when it is already first or absent.
 */
export function identityFirst(working: string[], identity: string): string[] {
  const at = working.indexOf(identity);
  if (at <= 0) return working;
  return [identity, ...working.filter((k) => k !== identity)];
}

/** Drops fixed keys not in `knownKeys`, warning once; never throws. Same reference if all known. */
export function warnUnknownFixedKeys(
  fixed: readonly string[],
  knownKeys: ReadonlySet<string>,
  warn: (message: string) => void = (message) => logger.warn(message),
): readonly string[] {
  const unknown = fixed.filter((k) => !knownKeys.has(k));
  if (unknown.length === 0) return fixed;
  warn(`[flow-layouts] fixedColumns names unknown column key(s), dropped: ${unknown.join(', ')}`);
  return fixed.filter((k) => knownKeys.has(k));
}
